/**
 * Lazy blob-URL resolver for a FileSource.
 *
 * Used by renderers that hand a URL directly to the browser (PDF iframe,
 * image, HTML iframe). Tracks loading / error / ready states and revokes
 * `URL.createObjectURL`-created URLs on unmount so blob-source previews
 * don't leak memory across dialog open/close cycles.
 *
 * Renderers pass `inline: true` when they need an in-page render — this
 * is how the PDF iframe gets a `Content-Disposition: inline` URL from
 * the server-side re-sign endpoint (and how artifact sources materialize
 * a blob URL with the right MIME).
 */
import { useEffect, useState } from "react";

import type { FileSource } from "../types";

export type FileBlobUrlState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error"; error: Error };

export function useFileBlobUrl(
  source: FileSource,
  opts?: { inline?: boolean },
): FileBlobUrlState {
  const [state, setState] = useState<FileBlobUrlState>({ status: "idle" });
  const inline = opts?.inline === true;

  /**
   * Key the effect on `source.id` (and the inline flag) rather than the
   * object identity — see {@link useFileText} for the same reasoning.
   * Callers that rebuild source factories every render would otherwise
   * refetch and re-create blob URLs on every parent re-render, which
   * also leaks memory because the revoke happens inside the cleanup.
   */
  useEffect(() => {
    if (!source.getBlobUrl) {
      setState({
        status: "error",
        error: new Error("Source does not support blob URL access"),
      });
      return;
    }

    let cancelled = false;
    let producedUrl: string | null = null;
    setState({ status: "loading" });

    source
      .getBlobUrl({ inline })
      .then((url) => {
        if (cancelled) {
          /**
           * Cleanup raced ahead of the resolve — revoke immediately so
           * we don't strand an unused object URL. Safe for non-blob URLs
           * too: revokeObjectURL on a non-blob URL is a no-op.
           */
          if (url.startsWith("blob:")) URL.revokeObjectURL(url);
          return;
        }
        producedUrl = url;
        setState({ status: "ready", url });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ status: "error", error });
      });

    return () => {
      cancelled = true;
      if (producedUrl && producedUrl.startsWith("blob:")) {
        URL.revokeObjectURL(producedUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: see comment above
  }, [source.id, inline]);

  return state;
}
